<template>
  <div class="pentagon-hero">
    <mdb-parallax
      v-if="!reducedMotion"
      :key="key"
      :src="mobile ? shown.compactSrc : shown.src"
      :height="mobile ? mobileHeight : 'full'"
      :factor="1"
      :alt="alt"
    />
    <div
      v-else
      class="pentagon-hero__static"
      :role="alt ? 'img' : null"
      :aria-label="alt || null"
      :style="{
        backgroundImage: `url(${mobile ? shown.compactSrc : shown.src})`,
        height: mobile ? `${mobileHeight}px` : '100vh',
      }"
    ></div>

    <div class="pentagon-hero__vignette"></div>

    <div class="pentagon-hero__layer">
      <div
        ref="content"
        class="pentagon-hero__content"
        :class="{ 'pentagon-hero__content--loading': loading }"
        :aria-busy="loading ? 'true' : null"
      >
        <h1 :key="title" class="pentagon-hero__title animated fadeInDown">{{ title }}</h1>
        <p
          v-if="motto"
          :key="motto"
          class="pentagon-hero__motto animated fadeInDown"
        >{{ motto }}</p>
        <p v-if="failed" class="pentagon-hero__notice" role="status">{{ $t('site.load_error') }}</p>
      </div>
    </div>

    <div class="pentagon-hero__fade"></div>
    <span class="pentagon-hero__cue" aria-hidden="true"></span>
  </div>
</template>

<script>
import { mdbParallax } from 'mdbvue';
import { prefersReducedMotion } from '../store/candles';

const MOBILE_BREAKPOINT = 768;

// The API returns absolute URLs where the built-in defaults are relative.
function sameImage(a, b) {
  return new URL(a, window.location.href).href === new URL(b, window.location.href).href;
}
const RESIZE_DEBOUNCE_MS = 200;
// How much slower than the scroll the title and motto move.
const CONTENT_SPEED = 0.25;

export default {
  name: 'HeroParallax',
  components: {
    mdbParallax,
  },
  props: {
    src: {
      type: String,
      required: true,
    },
    compactSrc: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    motto: {
      type: String,
      default: '',
    },
    alt: {
      type: String,
      default: '',
    },
    // The content is still being fetched / could not be fetched: the fallback copy stays.
    loading: {
      type: Boolean,
      default: false,
    },
    failed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      key: 0,
      // The images in use: a new source only replaces them once it has been preloaded.
      shown: { src: this.src, compactSrc: this.compactSrc },
      width: window.innerWidth,
      mobile: window.innerWidth < MOBILE_BREAKPOINT,
      // Fixed in pixels on mobile so the browser's address bar cannot make it jump.
      mobileHeight: Math.round(window.innerHeight * 0.7),
      reducedMotion: prefersReducedMotion(),
    };
  },
  watch: {
    src: 'swapImages',
    compactSrc: 'swapImages',
  },
  mounted() {
    this.resizeTimer = null;
    this.frame = null;
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);

    if (!this.reducedMotion) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
  },
  beforeDestroy() {
    this.destroyed = true;
    clearTimeout(this.resizeTimer);
    cancelAnimationFrame(this.frame);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    window.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    // Preload the image for this viewport so the re-measured parallax sees its real natural
    // height, then remount it. An identical source (the default photo) changes nothing.
    swapImages() {
      const next = { src: this.src, compactSrc: this.compactSrc };
      const wanted = this.mobile ? next.compactSrc : next.src;
      const current = this.mobile ? this.shown.compactSrc : this.shown.src;

      this.pendingSrc = wanted;

      if (sameImage(wanted, current)) {
        this.shown = next;

        return;
      }

      const probe = new Image();

      probe.onload = () => {
        if (this.pendingSrc === wanted && !this.destroyed) {
          this.shown = next;
          this.key += 1;
        }
      };
      probe.src = wanted;
    },
    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(this.measure, RESIZE_DEBOUNCE_MS);
    },
    // mdbParallax measures once, so a new key remounts it with the new viewport.
    measure() {
      const width = window.innerWidth;
      const mobile = width < MOBILE_BREAKPOINT;

      // On mobile a height-only resize is the address bar showing or hiding.
      if (mobile && mobile === this.mobile && width === this.width) {
        return;
      }

      this.width = width;
      this.mobile = mobile;
      this.mobileHeight = Math.round(window.innerHeight * 0.7);
      this.key += 1;
    },
    onScroll() {
      if (this.frame !== null) {
        return;
      }

      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const { content } = this.$refs;

        if (content) {
          content.style.transform = `translate3d(0, ${Math.round(window.pageYOffset * CONTENT_SPEED)}px, 0)`;
        }
      });
    },
  },
};
</script>

<style lang="scss">
.pentagon-hero {
  position: relative;
  z-index: 0;
  overflow: hidden;
  background-color: var(--pentagon-ink);
}

.pentagon-hero__static {
  background-position: center;
  background-size: cover;
}

// The photo of the page Laravel rendered, until the parallax replaces it.
.pentagon-hero__ssr {
  position: relative;
  z-index: 1;
  height: 100vh;
  height: 100svh;
  overflow: hidden;
}

// Placed like the parallax image at scroll position 0: natural size, centred, bottom aligned and
// moved down by the share of the overflow that mdb-parallax uses (half on desktop; on mobile the
// window height over the hero plus window height).
.pentagon-hero__ssr-image {
  position: absolute;
  bottom: 0;
  left: 50%;
  min-width: 100%;
  min-height: 100%;
  transform: translate(-50%, calc((100% - 100vh) / 2));
}

@media (max-width: 767.98px) {
  .pentagon-hero__ssr {
    height: 70vh;
    height: 70svh;
  }

  .pentagon-hero__ssr-image {
    transform: translate(-50%, calc((100% - 70vh) / 1.7));
  }
}

// Dark vignette over the photo.
.pentagon-hero__vignette {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  pointer-events: none;
  background:
    radial-gradient(ellipse at 50% 45%, rgba(11, 10, 9, 0.15) 0%, rgba(11, 10, 9, 0.7) 100%),
    rgba(11, 10, 9, 0.35);
}

.pentagon-hero__layer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 1rem;
}

.pentagon-hero__content {
  max-width: 1100px;
  margin-bottom: 4rem;
  text-align: center;
  will-change: transform;
}

.pentagon-hero__title {
  margin: 0;
  font-size: clamp(1.35rem, 5.4vw, 4.5rem);
  letter-spacing: 0.06em;
  text-shadow: 0 2px 24px rgba(0, 0, 0, 0.7);
}

.pentagon-hero__motto {
  margin: 1.5rem auto 0;
  max-width: 34em;
  color: var(--pentagon-ivory);
  font-size: clamp(1.05rem, 2.2vw, 1.6rem);
  font-style: italic;
  text-shadow: 0 1px 12px rgba(0, 0, 0, 0.8);
}

// While the content loads, the fallback copy pulses quietly.
.pentagon-hero__content--loading .pentagon-hero__title,
.pentagon-hero__content--loading .pentagon-hero__motto {
  animation: hero-loading 1.6s ease-in-out infinite;
}

.pentagon-hero__notice {
  margin: 1rem 0 0;
  color: var(--pentagon-parchment);
  font-size: 0.9rem;
  font-style: italic;
  opacity: 0.75;
}

@keyframes hero-loading {
  0%, 100% {
    opacity: 1;
  }

  50% {
    opacity: 0.55;
  }
}

// The photo fades into the page background instead of cutting off.
.pentagon-hero__fade {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 4;
  height: 40%;
  pointer-events: none;
  background: linear-gradient(to bottom, rgba(11, 10, 9, 0), var(--pentagon-ink));
}

.pentagon-hero__cue {
  position: absolute;
  bottom: 5.5rem;
  left: 50%;
  z-index: 5;
  width: 1px;
  height: 3rem;
  background: linear-gradient(to bottom, rgba(255, 215, 0, 0), rgba(255, 215, 0, 0.8));
  animation: hero-cue 2.4s ease-in-out infinite;
}

@keyframes hero-cue {
  0%, 100% {
    opacity: 0.35;
    transform: translateY(0);
  }

  50% {
    opacity: 1;
    transform: translateY(8px);
  }
}

@media (max-height: 480px) {
  .pentagon-hero__cue {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pentagon-hero__cue,
  .pentagon-hero__content--loading .pentagon-hero__title,
  .pentagon-hero__content--loading .pentagon-hero__motto {
    animation: none;
  }

  .pentagon-hero__content {
    will-change: auto;
  }
}
</style>
